import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization";
import { Input } from "@faire/web--source/slate/Input";
import { Dropdown } from "@faire/web--source/slate/Input/Dropdown";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";

import { DropdownGroups } from "../components/DropdownGroups";
import { validateEmail } from "../consts";
import { useLoggedOutContactUsContextStore } from "../hooks/LoggedOutContactUsContextStore";
import { useLoggedOutEmailContextStore } from "../hooks/LoggedOutEmailContextStore";
import { useLoggedOutIssuesDropdown } from "../hooks/useLoggedOutIssuesDropdown";
import { useLoggedOutUserType } from "../hooks/useLoggedOutUserType";
import {
  BUSINESS_NAME_PROMPT_MESSAGE,
  CUSTOMER_TYPE_PROMPT_MESSAGE,
  EMAIL_PROMPT_MESSAGE,
  getCustomerTypeList,
  TICKET_DETAIL_PROMPT_MESSAGE,
} from "../shared-text";

export const LoggedOutContactUsView: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const customerListType = getCustomerTypeList(strictLocalize);
  const [{ user }] = useLoggedOutContactUsContextStore(["user"]);
  const { isKnownUserType } = useLoggedOutUserType();
  const { handleUserDropdown } = useLoggedOutIssuesDropdown();
  const [
    {
      businessName,
      contactEmail,
      details,
      emailErrorMessage,
      descriptionErrorMessage,
      businessNameErrorMessage,
    },
    setLoggedOutEmailContextStore,
  ] = useLoggedOutEmailContextStore([
    "businessName",
    "contactEmail",
    "details",
    "emailErrorMessage",
    "descriptionErrorMessage",
    "businessNameErrorMessage",
    "formErrorMessage",
  ]);

  const handleTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLoggedOutEmailContextStore({
      details: e.target.value,
    });
  };

  const handleBusinessName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoggedOutEmailContextStore({
      businessName: e.target.value,
    });
  };

  const handleContactEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoggedOutEmailContextStore({
      contactEmail: e.target.value,
    });
  };

  const handleTextAreaBlur = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    strictLocalize: StrictLocalizeFunction
  ) => {
    if (e.target.value) {
      setLoggedOutEmailContextStore({
        descriptionErrorMessage: undefined,
      });
    } else {
      setLoggedOutEmailContextStore({
        descriptionErrorMessage: strictLocalize({
          defaultMessage: "Please enter a description",
        }),
      });
    }
  };

  const handleBusinessNameBlur = (
    e: React.ChangeEvent<HTMLInputElement>,
    strictLocalize: StrictLocalizeFunction
  ) => {
    if (e.target.value) {
      setLoggedOutEmailContextStore({
        businessNameErrorMessage: undefined,
      });
    } else {
      setLoggedOutEmailContextStore({
        businessNameErrorMessage: strictLocalize({
          defaultMessage: "Please enter a business name",
        }),
      });
    }
  };

  const handleContactEmailBlur = (
    e: React.ChangeEvent<HTMLInputElement>,
    strictLocalize: StrictLocalizeFunction
  ) => {
    const isValid = validateEmail(e.target.value);
    if (isValid) {
      setLoggedOutEmailContextStore({
        emailErrorMessage: undefined,
      });
    } else {
      setLoggedOutEmailContextStore({
        emailErrorMessage: strictLocalize({
          defaultMessage: "Please enter a valid email",
        }),
      });
    }
  };

  return (
    <Column data-test-id="logged-out-contact-us-view">
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          defaultMessage="Contact Us"
          description={{ text: "Title for a 'contact us' modal" }}
        />
      </Typography>
      <Spacer height="3x" />
      <Dropdown
        value={user}
        placeholder={CUSTOMER_TYPE_PROMPT_MESSAGE(strictLocalize)}
        options={customerListType}
        onSelect={handleUserDropdown}
      />
      <Spacer height="2x" />
      {isKnownUserType ? (
        <>
          <DropdownGroups />
          <Spacer height="2x" />
        </>
      ) : null}
      <Input
        aria-label="Email"
        value={contactEmail}
        placeholder={EMAIL_PROMPT_MESSAGE(strictLocalize)}
        onChange={handleContactEmail}
        onBlur={(e) => handleContactEmailBlur(e, strictLocalize)}
        error={emailErrorMessage}
      />
      <Spacer height="2x" />
      <Input
        aria-label="Business Name"
        value={businessName}
        placeholder={BUSINESS_NAME_PROMPT_MESSAGE(strictLocalize)}
        onChange={handleBusinessName}
        onBlur={(e) => handleBusinessNameBlur(e, strictLocalize)}
        error={businessNameErrorMessage}
      />
      <Spacer height="2x" />
      <TextArea
        aria-label="Description"
        label={strictLocalize({
          defaultMessage: "Description",
          description: {
            text: "Title for description to describe the issues you have",
          },
        })}
        placeholder={TICKET_DETAIL_PROMPT_MESSAGE(strictLocalize)}
        style={{ minHeight: "120px" }}
        value={details}
        onChange={handleTextArea}
        onBlur={(e) => handleTextAreaBlur(e, strictLocalize)}
        error={descriptionErrorMessage}
      />
      <Spacer height="2x" />
    </Column>
  );
};
